import { supabase } from "@/lib/supabase";
import { API_ENDPOINTS } from "../constants/api";
import { TOKEN_API } from "../constants/enviroment";

export interface AccountMember {
  id: number;
  account_id: string;
  user_id: string;
  email: string | null;
  full_name: string | null;
  role: string;
  status: string;
  invited_by: string | null;
  joined_at: string;
  created_at: string;
  updated_at: string;
}

const SESSION_ERROR = "Session expired. Please sign in again.";
const API_KEY_ERROR = "API key is not configured (VITE_TOKEN_API).";

async function getAuthParams(): Promise<{ userId: string; authHeader: string } | { error: string }> {
  const { data } = await supabase.auth.getSession();
  const userId = data.session?.user?.id;
  if (!userId) return { error: SESSION_ERROR };
  if (!TOKEN_API) return { error: API_KEY_ERROR };
  return { userId, authHeader: `Bearer ${TOKEN_API}` };
}

export async function getMembers(
  status?: string,
): Promise<AccountMember[] | { error: string }> {
  const params = await getAuthParams();
  if ("error" in params) return params;
  const { userId, authHeader } = params;

  const query = new URLSearchParams({ user_id: userId });
  if (status) query.set("status", status);

  const url = `${API_ENDPOINTS.account}members?${query.toString()}`;
  const res = await fetch(url, { headers: { Authorization: authHeader } });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = typeof body?.detail === "string" ? body.detail : "Failed to load members.";
    return { error: detail };
  }
  return res.json();
}

export type RemoveMemberResult = { ok: true } | { ok: false; error: string };

export async function removeMember(membershipId: number): Promise<RemoveMemberResult> {
  const params = await getAuthParams();
  if ("error" in params) return { ok: false, error: params.error };
  const { userId, authHeader } = params;

  const url = `${API_ENDPOINTS.account}members/${membershipId}?user_id=${encodeURIComponent(userId)}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: { Authorization: authHeader },
  });

  if (res.ok) return { ok: true };

  const body = await res.json().catch(() => ({}));
  const detail = typeof body?.detail === "string" ? body.detail : "Failed to remove member.";
  return { ok: false, error: detail };
}

export async function makeOwner(newOwnerId: string): Promise<RemoveMemberResult> {
  const params = await getAuthParams();
  if ("error" in params) return { ok: false, error: params.error };
  const { authHeader, userId } = params;

  const url = `${API_ENDPOINTS.account}transfer-ownership?user_id=${encodeURIComponent(userId)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: authHeader,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ new_owner_id: newOwnerId }),
  });

  if (res.ok) return { ok: true };

  const body = await res.json().catch(() => ({}));
  const detail = typeof body?.detail === "string" ? body.detail : "Failed to transfer ownership.";
  return { ok: false, error: detail };
}
